import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { getCategoryLabel, isAllHandsMealCategory } from './expenseCategories';

const PAGE_WIDTH = 612; // US Letter @ 72dpi
const PAGE_HEIGHT = 792;
const MARGIN = 50;
const INK = rgb(0.078, 0.106, 0.173); // #141b2c
const MUTED = rgb(0.247, 0.282, 0.290); // #3f484a
const RULE = rgb(0.749, 0.784, 0.788); // #bfc8c9
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = DATE_ONLY_PATTERN.exec(trimmed);
    if (match) {
      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;
      const day = Number(match[3]);
      const localDate = new Date(year, monthIndex, day);
      return localDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' });
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' });
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const isPdf = (contentType, fileName) =>
  /pdf/i.test(contentType || '') || /\.pdf$/i.test(fileName || '');

const isPng = (contentType, fileName) =>
  /png/i.test(contentType || '') || /\.png$/i.test(fileName || '');

const isJpg = (contentType, fileName) =>
  /jpe?g/i.test(contentType || '') || /\.jpe?g$/i.test(fileName || '');

// Wrap a string to fit a given width for the supplied font/size.
const wrapText = (text, font, size, maxWidth) => {
  const lines = [];
  String(text).split(/\r?\n/).forEach((paragraph) => {
    const words = paragraph.split(/\s+/);
    let current = '';
    words.forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, size) > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    });
    lines.push(current);
  });
  return lines;
};

/**
 * Builds a single PDF that contains a cover page of expense details followed by
 * every attachment baked in (PDF attachments are merged page-by-page, images are
 * drawn onto their own page). Returns a Uint8Array of the saved PDF.
 *
 * @param {object} expense - The expense record.
 * @param {Array<{ fileName: string, contentType: string, bytes: Uint8Array }>} attachments
 */
export async function buildExpenseReceiptPdf(expense = {}, attachments = []) {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const contentWidth = PAGE_WIDTH - MARGIN * 2;

  // ----- Cover page -----
  let page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let y = PAGE_HEIGHT - MARGIN;

  page.drawText('Expense Receipt', { x: MARGIN, y, size: 24, font: fontBold, color: INK });
  y -= 18;
  page.drawText(`Generated ${formatDate(new Date())}`, { x: MARGIN, y, size: 10, font, color: MUTED });
  y -= 22;
  page.drawLine({
    start: { x: MARGIN, y },
    end: { x: PAGE_WIDTH - MARGIN, y },
    thickness: 1,
    color: RULE,
  });
  y -= 24;

  const first = expense.first_name || expense.employee_first_name || '';
  const last = expense.last_name || expense.employee_last_name || '';
  const employeeName = `${first} ${last}`.trim();
  const approverName = `${expense.approver_first_name || ''} ${expense.approver_last_name || ''}`.trim()
    || expense.approver_email
    || expense.approved_by
    || (isAllHandsMealCategory(expense.category) && expense.status === 'approved' ? 'Pre-approved' : '');

  const rows = [
    ['Employee', employeeName],
    ['Purchase Date', formatDate(expense.purchase_date)],
    ['Vendor', expense.vendor_name],
    ['Customer', expense.customer_name],
    ['Category', getCategoryLabel(expense.category)],
    ['Amount', formatCurrency(expense.amount)],
    ['Notes', expense.description],
    ['Status', expense.status],
    ['Approved By', approverName],
    ['Approved On', expense.approved_at ? formatDate(expense.approved_at) : ''],
    ['Paid On', expense.paid_on ? formatDate(expense.paid_on) : ''],
    ['Review Comment', expense.approval_reason],
  ];

  const labelSize = 11;
  const valueSize = 12;
  const labelWidth = 130;
  const valueX = MARGIN + labelWidth;
  const valueWidth = contentWidth - labelWidth;
  const lineHeight = 16;

  rows.forEach(([label, rawValue]) => {
    const value = rawValue === null || rawValue === undefined || rawValue === '' ? '-' : String(rawValue);
    const valueLines = wrapText(value, font, valueSize, valueWidth);
    const rowHeight = Math.max(lineHeight, valueLines.length * lineHeight) + 8;

    if (y - rowHeight < MARGIN) {
      page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      y = PAGE_HEIGHT - MARGIN;
    }

    page.drawText(label, { x: MARGIN, y: y - 2, size: labelSize, font: fontBold, color: MUTED });
    valueLines.forEach((line, index) => {
      page.drawText(line, {
        x: valueX,
        y: y - 2 - index * lineHeight,
        size: valueSize,
        font,
        color: INK,
      });
    });

    y -= rowHeight;
    page.drawLine({
      start: { x: MARGIN, y: y + 4 },
      end: { x: PAGE_WIDTH - MARGIN, y: y + 4 },
      thickness: 0.5,
      color: RULE,
    });
    y -= 6;
  });

  // ----- Attachments -----
  for (const attachment of attachments) {
    const { fileName, contentType, bytes } = attachment || {};
    if (!bytes || !bytes.length) continue;

    try {
      if (isPdf(contentType, fileName)) {
        const source = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const copied = await pdf.copyPages(source, source.getPageIndices());
        copied.forEach((p) => pdf.addPage(p));
        continue;
      }

      if (isPng(contentType, fileName) || isJpg(contentType, fileName)) {
        const image = isPng(contentType, fileName)
          ? await pdf.embedPng(bytes)
          : await pdf.embedJpg(bytes);

        const imgPage = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
        imgPage.drawText(fileName || 'Attachment', {
          x: MARGIN,
          y: PAGE_HEIGHT - MARGIN + 6,
          size: 10,
          font,
          color: MUTED,
        });

        const maxW = contentWidth;
        const maxH = PAGE_HEIGHT - MARGIN * 2;
        const scale = Math.min(maxW / image.width, maxH / image.height, 1);
        const w = image.width * scale;
        const h = image.height * scale;
        imgPage.drawImage(image, {
          x: (PAGE_WIDTH - w) / 2,
          y: (PAGE_HEIGHT - h) / 2,
          width: w,
          height: h,
        });
        continue;
      }

      // Unsupported type — leave a placeholder page so it is not silently dropped.
      const notePage = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      notePage.drawText('Attachment could not be embedded', {
        x: MARGIN,
        y: PAGE_HEIGHT - MARGIN,
        size: 14,
        font: fontBold,
        color: INK,
      });
      notePage.drawText(`${fileName || 'Attachment'} (${contentType || 'unknown type'})`, {
        x: MARGIN,
        y: PAGE_HEIGHT - MARGIN - 22,
        size: 11,
        font,
        color: MUTED,
      });
    } catch {
      const errPage = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      errPage.drawText('Attachment could not be embedded', {
        x: MARGIN,
        y: PAGE_HEIGHT - MARGIN,
        size: 14,
        font: fontBold,
        color: INK,
      });
      errPage.drawText(fileName || 'Attachment', {
        x: MARGIN,
        y: PAGE_HEIGHT - MARGIN - 22,
        size: 11,
        font,
        color: MUTED,
      });
    }
  }

  return pdf.save();
}

export function buildReceiptFileName(expense = {}) {
  const vendor = String(expense.vendor_name || 'expense').replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '');
  const date = String(expense.purchase_date || '').slice(0, 10);
  return `Expense-Receipt-${vendor || 'expense'}${date ? `-${date}` : ''}.pdf`;
}
